// src/lib/mastery-display.ts
//
// Shared THEME-AWARE display vocabulary for topic mastery tiers, used by both
// the dashboard Mastery Map and the Practice-by-Topic page so the two views
// speak the same visual language (same tier colors, same legend, same medals).
//
// Why not MASTERY_DISPLAY.cellClass from sat-domains.ts: those classes are
// dark-tuned (light text on dark tints) and unreadable on light pages. Tier
// LOGIC (masteryTier, thresholds, icons, labels) still lives in sat-domains —
// this module only owns the light+dark Tailwind class pairs.

import type { MasteryTier } from '@/lib/test-engine/sat-domains'
import { Circle, CircleDot, Medal, Award, Trophy, type LucideIcon } from 'lucide-react'

/** Cell/badge classes per tier — readable in BOTH themes.
 *
 * The three medal tiers share a metallic gradient treatment with an
 * ASCENDING shine: bronze (warm, muted) < silver (cool, polished) < gold
 * (bright). Silver previously used the same flat slate family as the
 * `untouched` state — it read as "nothing" and looked WEAKER than bronze
 * (Visakh's legend report). The gradient + stronger border give silver a
 * clearly-richer-than-bronze look while keeping the untouched state flat
 * and faint, so earned tiers visibly pop against unearned cells. */
export const TIER_CELL: Record<MasteryTier, string> = {
  untouched:
    'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400',
  in_progress:
    'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-900/40 dark:text-rose-200',
  // Bronze is a COPPER/orange family and gold a bright YELLOW — deliberately
  // different hues so the two medals never read as the same color. (They used
  // to be amber-400 vs yellow-500 — adjacent on the warm spectrum, hence the
  // clash.) Order now reads bronze (copper) < silver (slate) < gold (yellow).
  bronze:
    'border-orange-600 bg-gradient-to-br from-orange-100 to-orange-300 text-orange-900 dark:border-orange-500/60 dark:from-orange-950/50 dark:to-orange-800/40 dark:text-orange-200',
  silver:
    'border-slate-400 bg-gradient-to-br from-slate-100 to-slate-300 text-slate-800 dark:border-slate-300/60 dark:from-slate-600/50 dark:to-slate-800/50 dark:text-slate-50',
  gold: 'border-yellow-400 bg-gradient-to-br from-yellow-100 to-yellow-300 text-yellow-900 dark:border-yellow-300/70 dark:from-yellow-800/50 dark:to-yellow-600/40 dark:text-yellow-50',
}

/** Border-accent-only classes per tier (for larger cards that keep their own bg). */
export const TIER_ACCENT: Record<MasteryTier, string> = {
  untouched: 'border-border/70',
  in_progress: 'border-rose-300 dark:border-rose-500/40',
  bronze: 'border-orange-600 dark:border-orange-500/60',
  silver: 'border-slate-400 dark:border-slate-300/60',
  gold: 'border-yellow-400 dark:border-yellow-300/70',
}

/** A distinct lucide icon per tier — a SHAPE signal that stacks with the tier
 *  color, replacing the old Unicode emoji medals (🥉🥈🥇) which rendered tiny,
 *  low-contrast, and near-identical across the three metallic tiers. Earned
 *  tiers ascend Medal → Award → Trophy; the two unearned tiers use plain rings
 *  so they read as "not a medal yet". Rendered via <MasteryMedal>/<TierIcon>. */
export const TIER_ICON: Record<MasteryTier, LucideIcon> = {
  untouched: Circle,
  in_progress: CircleDot,
  bronze: Medal,
  silver: Award,
  gold: Trophy,
}

/** Low→high mastery, the canonical legend order. */
export const LEGEND_ORDER: MasteryTier[] = ['untouched', 'in_progress', 'bronze', 'silver', 'gold']
